<script setup lang="ts">
withDefaults(defineProps<{
  layout?: 'Centered' | 'Left-Aligned'
}>(), {
  layout: 'Centered',
})

// Scroll indicator — hide after first scroll
const scrollHidden = ref(false)

onMounted(() => {
  const onScroll = () => {
    if (!scrollHidden.value && window.scrollY > 40) {
      scrollHidden.value = true
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true })
  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll)
  })
})
</script>

<template>
  <!-- Centered layout -->
  <section
    v-if="layout === 'Centered'"
    class="relative flex flex-col gap-6 h-[620px] md:min-h-screen items-center justify-center px-4 md:px-20 py-14 w-full overflow-hidden"
  >
    <!-- Background video with Ken Burns -->
    <video
      class="hero-video absolute inset-0 w-full h-full object-cover pointer-events-none"
      autoplay
      loop
      muted
      playsinline
      preload="metadata"
      aria-hidden="true"
    >
      <source src="/hero-loop.webm" type="video/webm" />
      <source src="/hero-loop.mp4" type="video/mp4" />
    </video>

    <!-- Gradient overlay -->
    <div class="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/70 pointer-events-none" aria-hidden="true" />

    <!-- Content -->
    <div class="flex flex-col gap-7 w-full max-w-7xl z-10">
      <div class="flex flex-col items-center text-white whitespace-nowrap">
        <p class="hero-eyebrow font-label font-light text-xs md:text-lg tracking-wider text-brand">
          A STORIES OF BEAUTY, LAND AND CRAFT
        </p>
        <h1 class="hero-heading font-serif font-normal text-3xl md:text-7xl text-center tracking-wider md:tracking-wider leading-relaxed text-white">
          <span class="block leading-tight">For Those Who Move</span>
          <span class="block leading-tight">Through the World</span>
          <span class="block leading-tight">With Purpose</span>
        </h1>
      </div>

      <!-- CTA buttons -->
      <nav class="hero-ctas flex gap-10 items-start justify-center overflow-hidden" aria-label="Hero actions">
        <NuxtLink to="/journal">
          <AppButton type="Ghost" size="Small" label="Read the Stories" :show-icon-right="true" class="md:hidden" />
          <AppButton type="Ghost" size="Large" label="Read the Stories" :show-icon-right="true" class="hidden md:inline-flex" />
        </NuxtLink>
        <NuxtLink to="/shop">
          <AppButton type="Ghost" size="Small" label="Explore the Goods" :show-icon-right="true" class="md:hidden" />
          <AppButton type="Ghost" size="Large" label="Explore the Goods" :show-icon-right="true" class="hidden md:inline-flex" />
        </NuxtLink>
      </nav>
    </div>

    <!-- Scroll indicator -->
    <div
      class="hero-scroll absolute bottom-9 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 transition-opacity duration-300"
      :class="scrollHidden && 'opacity-0 pointer-events-none'"
      aria-hidden="true"
    >
      <div class="w-px h-7 bg-gradient-to-b from-transparent to-brand/60" />
      <div class="w-2.5 h-2.5 border-r border-b border-brand/70 rotate-45 -mt-1" />
    </div>
  </section>

  <!-- Left-Aligned layout -->
  <section
    v-else
    class="relative bg-page flex flex-col gap-5 h-[616px] items-start justify-center px-20 py-24 w-full whitespace-nowrap overflow-hidden"
  >
    <p class="font-label font-light text-xs leading-4 tracking-wider text-brand">
      FEATURED STORY
    </p>
    <div class="font-serif font-normal text-navy text-5xl">
      <p class="leading-[58px] mb-0">The Salt Flats of</p>
      <p class="leading-[58px]">Bonaire at Dawn</p>
    </div>
    <p class="font-sans font-normal text-base leading-6 text-secondary whitespace-normal max-w-[560px]">
      In the early hours before the world wakes, there is a stillness that belongs only to water and light.
    </p>
    <NuxtLink to="/journal">
      <AppButton type="Ghost" size="Small" label="Read the Story" :showIconRight="true" />
    </NuxtLink>
  </section>
</template>

<style scoped>
/* Ken Burns slow zoom on video */
@keyframes kenBurns {
  from { transform: scale(1.0);  transform-origin: 52% 48%; }
  to   { transform: scale(1.05); transform-origin: 48% 52%; }
}

.hero-video {
  animation: kenBurns 10s ease-in-out infinite alternate;
}

/* Staggered fade-up entrance */
@keyframes fadeUp {
  to { opacity: 1; transform: translateY(0); }
}

.hero-eyebrow {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeUp 700ms ease-out 0ms forwards;
}

.hero-heading {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeUp 700ms ease-out 150ms forwards;
}

.hero-ctas {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeUp 700ms ease-out 300ms forwards;
}

/* Scroll indicator pulse */
@keyframes scrollPulse {
  0%   { opacity: 1; }
  50%  { opacity: 0.3; }
  100% { opacity: 1; }
}

.hero-scroll {
  opacity: 0;
  animation: fadeUp 700ms ease-out 900ms forwards,
             scrollPulse 2s ease-in-out 1.6s infinite;
}
</style>
